import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';

// Placeholder projects - these would be replaced with actual portfolio items
const projects = [
    {
        id: 1,
        title: 'Project Title One',
        category: 'Brand Identity',
        description: 'Brief description of the project challenge and outcome.',
        link: '/work/project-1',
        external: false,
    },
    {
        id: 2,
        title: 'Project Title Two',
        category: 'Communication Design',
        description: 'Brief description of the project challenge and outcome.',
        link: 'https://www.behance.net/ceilkey',
        external: true,
    },
    {
        id: 3,
        title: 'Project Title Three',
        category: 'Illustration',
        description: 'Brief description of the project challenge and outcome.',
        link: 'https://www.behance.net/ceilkey',
        external: true,
    },
];

function Work() {
    useEffect(() => {
        document.title = "Work — Deepali Agrawal";
    }, []);

    return (
        <div className="section-padding">
            {/* Intro Section */}
            <section className="max-w-3xl mb-16 md:mb-24">
                <h1 className="text-3xl md:text-headline font-fraunces mb-8">
                    Work
                </h1>

                <div className="space-y-6 text-body-lg text-warm-gray-600">
                    <p>
                        Every project here began with a question — not a style.
                    </p>
                    <p>
                        I approach design as a process of understanding first: the brand, the problem, the people it's meant for. The visual language comes later, shaped by context rather than trends.
                    </p>
                    <p>
                        The projects below reflect that approach — across branding, communication design, and illustration-led work.
                    </p>
                </div>
            </section>

            {/* Projects Grid */}
            <section className="mb-16 md:mb-24">
                <div className="grid gap-8 md:gap-12">
                    {projects.map((project) => (
                        <article
                            key={project.id}
                            className="group border-b border-warm-gray-200 pb-8 md:pb-12"
                        >
                            {project.external ? (
                                <a
                                    href={project.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block"
                                >
                                    <ProjectContent project={project} />
                                </a>
                            ) : (
                                <Link to={project.link} className="block">
                                    <ProjectContent project={project} />
                                </Link>
                            )}
                        </article>
                    ))}
                </div>
            </section>

            {/* View Full Portfolio CTA */}
            <section className="mb-16">
                <a
                    href="https://www.behance.net/ceilkey"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2"
                >
                    View Full Portfolio on Behance
                    <ExternalLink size={16} />
                </a>
            </section>

            {/* How Projects Are Presented */}
            <section className="max-w-3xl py-12 md:py-16 border-t border-warm-gray-200">
                <h2 className="text-xl md:text-title font-fraunces mb-6">
                    How Projects Are Presented
                </h2>

                <div className="space-y-4 text-body text-warm-gray-600 mb-8">
                    <p>Each project includes:</p>
                    <ul className="space-y-2 ml-4">
                        <li className="flex items-start gap-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" />
                            <span>The context and challenge</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" />
                            <span>My role and thought process</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" />
                            <span>Key visual outcomes</span>
                        </li>
                    </ul>
                </div>

                <p className="text-body text-warm-gray-500 italic">
                    Some projects are client-led, some are collaborative, and some are self-initiated — all of them reflect how I think and work as a designer.
                </p>
            </section>

            {/* Closing Line */}
            <section className="max-w-3xl py-12 md:py-16 border-t border-warm-gray-200">
                <p className="text-xl font-fraunces text-warm-gray-700 mb-6">
                    I'm interested in work that values thinking as much as execution.
                </p>
                <p className="text-body-lg text-warm-gray-600">
                    If a project resonates with you, chances are the process will too.
                </p>
            </section>
        </div>
    );
}

function ProjectContent({ project }) {
    return (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
                <span className="text-sm text-warm-gray-500 mb-2 block">
                    {project.category}
                </span>
                <h3 className="text-xl md:text-2xl font-fraunces text-warm-gray-900 group-hover:text-accent transition-colors mb-2">
                    {project.title}
                </h3>
                <p className="text-warm-gray-600">
                    {project.description}
                </p>
            </div>
            <div className="flex items-center text-warm-gray-500 group-hover:text-accent transition-colors">
                {project.external ? (
                    <ExternalLink size={20} />
                ) : (
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                )}
            </div>
        </div>
    );
}

export default Work;
